import { Icon } from 'app/components/common/Icon';
import { formatNum } from 'app/utils';
import { Steart } from 'app/models/core/steart.model';
import { UserModel, UserStatus } from 'app/models/core/user.model';
import { CSSProperties } from 'react';
import colors from 'assets/scss/colors.module.scss';

interface IPairedBarsProps {
    curr: number;
    prev: number;
    max: number;
    formatValue: (v: number) => string;
}

// zwei Balken pro Zelle: aktuelles Jahr (gelb) über Vorjahr (blau), Wert im Balken
export const PairedBars = (props: IPairedBarsProps) => {
    const bar = (value: number, color: string, marginTop: number) => {
        const width = props.max > 0 ? Math.min((value / props.max) * 100, 100) : 0;
        return (
            <div
                style={{
                    width: `${width}%`,
                    minWidth: 'fit-content',
                    background: color,
                    borderRadius: 2,
                    padding: '0.15rem 0.3rem',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    marginTop,
                }}
            >
                {props.formatValue(value)}
            </div>
        );
    };
    return (
        <div style={{ width: '100%', minWidth: 120 }}>
            {bar(props.curr, colors.yellow, 0)}
            {bar(props.prev, colors.blue, 4)}
        </div>
    );
};

export const swatch = (color: string): CSSProperties => ({
    display: 'inline-block',
    width: '1.5rem',
    height: '0.8rem',
    borderRadius: 2,
    background: color,
    marginRight: '0.3rem',
    verticalAlign: 'middle',
});

export const YearLegend = (props: { currYear: number; prevYear: number }) => {
    return (
        <div style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem' }}>
            <span style={swatch(colors.yellow)} />
            <span style={{ marginRight: '1rem' }}>{props.currYear}</span>
            <span style={swatch(colors.blue)} />
            <span>{props.prevYear}</span>
        </div>
    );
};

interface IColorInfoBoxProps {
    currYear: number;
    prevYear: number;
    currText?: string;
    prevText?: string;
}

export const ColorInfoBox = (props: IColorInfoBoxProps) => {
    const currText =
        props.currText ??
        `Anmeldequote ${props.currYear} — Anteil der aktiven Benutzer, die sich seit dem 1.1.${props.currYear} mindestens einmal angemeldet haben (Ziel: 100%).`;
    const prevText =
        props.prevText ?? `Vorjahresquote ${props.prevYear} — gleicher Zeitraum (1.1. bis heute) im Vorjahr, bezogen auf die heutigen aktiven Benutzer.`;
    return (
        <div className="note-box content">
            <p>
                <Icon iconClass="info-circle" />
            </p>
            <div className="note-message">
                <p>
                    <span style={swatch(colors.yellow)} />
                    <strong>Gelb:</strong> {currText}
                </p>
                <p>
                    <span style={swatch(colors.blue)} />
                    <strong>Blau:</strong> {prevText}
                </p>
            </div>
        </div>
    );
};

export const QuoteTotals = (props: { rows: { activeCount: number; loginCount: number; prevLoginCount: number }[]; currYear: number; prevYear: number }) => {
    const totalActive = props.rows.reduce((sum, r) => sum + r.activeCount, 0);
    const totalLogins = props.rows.reduce((sum, r) => sum + r.loginCount, 0);
    const totalPrevLogins = props.rows.reduce((sum, r) => sum + r.prevLoginCount, 0);
    const totalQuote = totalActive > 0 ? Math.round((totalLogins / totalActive) * 1000) / 10 : 0;
    const totalPrevQuote = totalActive > 0 ? Math.round((totalPrevLogins / totalActive) * 1000) / 10 : 0;

    return (
        <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.85rem', marginTop: '0.5rem', color: colors.grey }}>
            <span>Aktive Benutzer gesamt: {formatNum(totalActive)}</span>
            <span>
                Angemeldet {props.currYear}: {formatNum(totalLogins)} ({totalQuote}%)
            </span>
            <span>
                Angemeldet {props.prevYear}: {formatNum(totalPrevLogins)} ({totalPrevQuote}%)
            </span>
        </div>
    );
};

// Aktive Benutzer im Sinne der Anmeldequote: ohne DVSADMIN, LSV, GSL (noch nicht Teil der Anwendung)
// und unbekannte Stellenarten
export const isRelevantUser = (u: UserModel): boolean => {
    if (u.status !== UserStatus.active) {
        return false;
    }
    if (!u.steart || u.steart.name.toLowerCase() === 'unbekannt') {
        return false;
    }
    if (u.steart.steart === Steart.DVSADMIN || u.steart.steart === Steart.LSV || u.steart.steart === Steart.GSL) {
        return false;
    }
    return true;
};
